import React from 'react';
import axios from 'axios';
import Dropzone from "react-dropzone";
import {Button,Divider, Box, Card, CardActions, CardMedia} from '@mui/material';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import * as ETVConstants from '../ETVConstants';
import { handleAuthenticationFailure } from '../tools/AuthSession';
import ETVMessagePopup from '../tools/ETVMessagePopup';

const MAX_BACKGROUND_SIZE = 5 * 1024 * 1024;

const errorMessage = (error, fallback = 'The background image could not be uploaded.') => {
	const responseData = error?.response?.data;
	if(typeof responseData === 'string' && responseData.trim()) return responseData.trim();
	return responseData?.reason || responseData?.error || responseData?.message
		|| error?.message || fallback;
};

class SelectBackground extends React.Component {

    state = {
		list:[],
		current_bg: undefined,
		bg_color: "#000000",
		hasChanged:false,
		uploading:false,
		uploadError:'',
    }

	componentDidUpdate(prevProps, prevState) {
		if(!this.state.isLoaded) {
			this.loadItems();
		}
		if(prevProps.device_id !== this.props.device_id) {
			this.loadItems();
		}
	}

    componentDidMount() {
      	this.loadItems();
    }

    loadItems() {
		var accessToken = localStorage.getItem("etv.access_token");
		var reqUserID = localStorage.getItem("etv.user_id");
		var url = ETVConstants.getServerBase()+"/device_mgmt?action=get_backgrounds&device_id="+this.props.device_id+"&user_id="+this.props.user_id;
		if(this.props.group_id>0) {
			url = ETVConstants.getServerBase()+"/device_mgmt?action=get_backgrounds&group_id="+this.props.group_id+"&user_id="+this.props.user_id;
		}
		fetch(url,{ headers: { 'user_id':reqUserID, 'access_token': accessToken }})
        .then(async res => {
			const result = await res.json();
			handleAuthenticationFailure(res, result);
			if(!res.ok || result?.result === false) {
				throw new Error(result?.error || result?.reason || `http_${res.status}`);
			}
			return result;
		})
        .then(
          (result) => {
			var currentBG = result['current'] || "";
			this.setState({list:Array.isArray(result.list)?result.list:[], isLoaded:true, current_bg:currentBG, error:undefined});
			if(currentBG.startsWith("#")) {
				this.setState({bg_color:currentBG});
			}else{
				this.setState({bg_color:"#000000"});
			}
          },
          (error) => {
            this.setState({
              current_bg: undefined,
			  list:[],
			  isLoaded:true,
			  error,
            });
          }
        )
    }

	saveBGColor() {
		this.setBGImage(this.state.bg_color, true);
	}

	setBGImage( bgImg, closeAfter = false) {
		var accessToken = localStorage.getItem("etv.access_token");
		var reqUserID = localStorage.getItem("etv.user_id");
		var url = ETVConstants.getServerBase()+"/device_mgmt?action=set_background&device_id="+this.props.device_id+"&background_image="+encodeURIComponent(bgImg);
		if(this.props.group_id>0) {
			url = ETVConstants.getServerBase()+"/device_mgmt?action=set_background&group_id="+this.props.group_id+"&background_image="+encodeURIComponent(bgImg);
		}
		//console.log("set bg url="+url);
		fetch(url,{ headers: { 'user_id':reqUserID, 'access_token': accessToken }})
		.then(res => res.json())
		.then(
			(result) => {
				//console.log("result="+JSON.stringify(result));
				this.setState({isLoaded:false,hasChanged:true}, () => {
					this.props.onChanged?.();
					if(closeAfter) this.props.closeF(true);
				});
			},
			(error) => {
			this.setState({
				current_bg: undefined,
			});
			}
		)
	}

	deleteBGImage( uri) {
		var accessToken = localStorage.getItem("etv.access_token");
		var reqUserID = localStorage.getItem("etv.user_id");
		var url = ETVConstants.getServerBase()+"/device_mgmt?action=delete_background&background_id="+uri;
		fetch(url,{ headers: { 'user_id':reqUserID, 'access_token': accessToken }})
			.then(res => res.json())
			.then(
				(result) => {
					this.setState({isLoaded:false});
				},
				(error) => {
					this.setState({
						current_bg: undefined,
					});
				}
			)
	}

	createBackground(jsonObj) {
		var url = jsonObj.public_url;
		var uri = jsonObj.uri;
		var hasBorder = (url===this.state.current_bg);
		return (
			<Card
				key={uri}
				variant="outlined"
				sx={{
					display:'flex',
					flexDirection:'column',
					height:270,
					minWidth:0,
					borderWidth:hasBorder?2:1,
					borderColor:hasBorder?'primary.main':'divider',
					boxShadow:hasBorder?'0 0 0 2px rgba(21, 101, 192, 0.10)':'none',
				}}
			>
				<CardMedia 
					component="img" 
					image={ETVConstants.ON_PREMISE_VERSION?ETVConstants.makeContentURLRelative(url):url} 
					sx={{
						width:'100%',
						height:200,
						objectFit:'cover',
						bgcolor:'grey.100',
						}}
				/>
				<CardActions sx={{mt:'auto',minHeight:68,p:1.25,justifyContent:'space-between'}}>
					{!hasBorder?
						<Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%',gap:1}}>
							<Button size='small' variant='contained' color='primary' onClick={()=>{this.setBGImage(url)}}>{ETVConstants.trans("set_background")}</Button>
							<IconButton size='small' variant='outlined' color="error" onClick={()=>{this.deleteBGImage(uri)}}><DeleteIcon/></IconButton>
						</Box>
						:
						<Button size='small' variant='contained' color='error' startIcon={<DeleteIcon/>} onClick={()=>{this.setBGImage("")}}>{ETVConstants.trans("remove_background")}</Button>
					}
				</CardActions>
			</Card>
			)
	}

	onDrop = async (files, fileRejections = []) => {
		if(fileRejections.length > 0) {
			const rejection = fileRejections[0];
			const reason = rejection.errors?.map(item => item.message).join(' ') ||
				'Please select a JPG or PNG image smaller than 5 MB.';
			this.setState({uploadError:reason});
			return;
		}
		if(files.length === 0) {
			this.setState({uploadError:'Please select a JPG or PNG image smaller than 5 MB.'});
			return;
		}

		var targetDir = "/"+this.props.user_id+"/backgrounds";
		const file = files[0];
		this.setState({uploading:true, uploadError:''});
		try {
			if(ETVConstants.ON_PREMISE_VERSION) {
				await this.uploadFileToOnPremise(file, targetDir);
			}else{
				await this.uploadFileToGCS(file, targetDir);
			}
			this.setState({isLoaded:false, uploading:false});
		}catch(error) {
			console.error('Background upload failed', error);
			this.setState({uploading:false, uploadError:errorMessage(error)});
		}
	}

	updateProgress( size, loaded) {
		//console.log("size="+size+" loaded="+loaded);
	}

	async uploadFileToOnPremise(file, targetDir) {
		var filename = file.name;
		var accessToken = localStorage.getItem("etv.access_token");
		var reqUserID = localStorage.getItem("etv.user_id");
		var formData = new FormData();
		formData.append('file', file);
		formData.append('filename', filename);
		formData.append('target_dir',targetDir);
		const config = {
			headers: {	'content-type':'multipart/form-data', 'user_id':reqUserID, 'access_token': accessToken },
			onUploadProgress: progressEvent => this.updateProgress(file.size, progressEvent.loaded)
		}
		var url = ETVConstants.getServerBase()+"/file_premise_upload";
		const response = await axios.post(url, formData, config);
		if(response.data?.result === false) {
			throw new Error(response.data.reason || response.data.error ||
				'The server rejected the background image.');
		}
	}

	async uploadFileToGCS(file, targetDir) {
		var filename = file.name;
		var accessToken = localStorage.getItem("etv.access_token");
		var reqUserID = localStorage.getItem("etv.user_id");
		const params = new URLSearchParams({
			action:'request_upload',
			target_dir:targetDir,
			filename,
		});
		const url = ETVConstants.getServerBase()+"/gcs_upload?"+params.toString();
		const response = await fetch(url, {
			headers:{'user_id':reqUserID, 'access_token':accessToken},
		});
		const responseText = await response.text();
		let result;
		try {
			result = JSON.parse(responseText);
		}catch(error) {
			throw new Error(responseText || `Upload request failed (HTTP ${response.status}).`);
		}
		handleAuthenticationFailure(response, result);
		if(!response.ok || result?.result === false || !result?.url || !result?.fields) {
			throw new Error(result?.reason || result?.error ||
				`Upload request failed (HTTP ${response.status}).`);
		}

		const formData = new FormData();
		Object.entries(result.fields).forEach(([key,value]) => formData.append(key,value));
		formData.append('file', file);
		// Let the browser add the multipart boundary required by the signed POST.
		await axios.post(result.url, formData);
		await this.givePublicAccess(targetDir, filename);
	}

	async givePublicAccess(targetDir, filename) {
		const accessToken = localStorage.getItem('etv.access_token');
		const reqUserID = localStorage.getItem('etv.user_id');
		const params = new URLSearchParams({
			action:'public_access',
			target_dir:targetDir,
			filename,
		});
		const response = await fetch(
			ETVConstants.getServerBase()+"/gcs_upload?"+params.toString(),
			{headers:{'user_id':reqUserID, 'access_token':accessToken}}
		);
		const responseText = await response.text();
		let result;
		try {
			result = JSON.parse(responseText);
		}catch(error) {
			throw new Error(responseText || `Finalizing upload failed (HTTP ${response.status}).`);
		}
		handleAuthenticationFailure(response, result);
		if(!response.ok || result?.result === false) {
			throw new Error(result?.reason || result?.error ||
				`Finalizing upload failed (HTTP ${response.status}).`);
		}
		return result;
	}

	setBGFingerprint(deviceID, fp) {
		var url = ETVConstants.getServerBase()+"/device_mgmt?action=set_bg_fingerprint&device_id="+deviceID+"&fingerprint="+fp;
		ETVConstants.sendCMDToServer(url);
	}

	closeBackgroundSelection() {
		this.props.closeF(this.state.hasChanged);
	}

    render() {
		//<div id='wrapper' style={{width:'100%',border:'',overflow:'hidden',marginTop:10}}>
		return(
			<Box>
				<ETVMessagePopup
					open={Boolean(this.state.uploadError)}
					onClose={()=>this.setState({uploadError:''})}
					type="error"
					title={ETVConstants.trans('uploading_error')}
					message={this.state.uploadError}
				/>
				<Box sx={{p:2,borderRadius:2,bgcolor:'action.hover',mb:3}}>
					<Typography variant="h6" fontWeight={700}>{ETVConstants.trans("background_color")}</Typography>
					<Typography variant="body2" color="text.secondary" sx={{mb:1.5}}>Choose a solid color instead of an image background.</Typography>
					<Box sx={{display:'flex',alignItems:'center',gap:1.5,flexWrap:'wrap'}}>
						<input type="color" style={{width:56,height:40,padding:2,border:'1px solid #ccd3da',borderRadius:6}} value={this.state.bg_color} onChange={e => this.setState({bg_color:e.target.value})} />
						<Button variant='contained' color='primary' onClick={()=>this.saveBGColor()}>{ETVConstants.trans("save_background_color")}</Button>
					</Box>
				</Box>

				<Typography variant="h6" fontWeight={700} sx={{mb:1}}>{ETVConstants.trans("background_select")}</Typography>
				<Box>
						<Dropzone
								onDrop={this.onDrop}
								disabled={this.state.uploading}
								multiple={false}
								accept={{
									'image/jpeg':['.jpg', '.jpeg'],
									'image/png':['.png'],
								}}
								minSize={1024}
								maxSize={MAX_BACKGROUND_SIZE}
								style={{width:'100%',height:'100%'}}>

						{({ getRootProps, getInputProps }) => (
							<div {...getRootProps({ className: "dropzone" })}>
									<input {...getInputProps()} />
									<p>{this.state.uploading
										? ETVConstants.trans('uploading...')
										: ETVConstants.trans("click_to_upload_files_max_5mb")}</p>
							</div>
						)}
						</Dropzone>
				</Box>
				<Divider sx={{my:2.5}} />
				<Box sx={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))',gap:2}}>
					{this.state.list.map(item => this.createBackground(item))}
				</Box>
			</Box>
		);
	}
}
export default SelectBackground
